import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, Trash2, GripVertical, Save, X, Loader2, 
  Play, ChevronDown, ChevronUp, Upload, Film, Video, CheckCircle, AlertCircle
} from 'lucide-react';
import api from '../../services/api';
import { checkYouTubeEmbeddable } from '../../utils/youtubeEmbedCheck';

// Episode Upload Modal with Mux integration
const EpisodeUploadModal = ({ episode, onClose, onUploadComplete }) => {
  const [uploadUrl, setUploadUrl] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, getting-url, ready, uploading, processing, complete, error
  const [error, setError] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [simulatedProgress, setSimulatedProgress] = useState(null);

  useEffect(() => {
    // Get upload URL when modal opens
    getUploadUrl();
    
    // Listen for progress messages from iframe
    const handleMessage = (event) => {
      if (event.data.type === 'upload-started') {
        handleUploadStarted();
      } else if (event.data.type === 'upload-complete') {
        handleUploadComplete();
      } else if (event.data.type === 'upload-progress') {
        const progress = event.data.progress || 0;
        setUploadProgress(progress);
        // Clear simulated progress if we're getting real progress
        if (simulatedProgress) {
          clearInterval(simulatedProgress);
          setSimulatedProgress(null);
        }
      } else if (event.data.type === 'upload-error') {
        setError(event.data.error || 'Upload failed');
        setStatus('error');
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
      if (simulatedProgress) clearInterval(simulatedProgress);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const getUploadUrl = async () => {
    setStatus('getting-url');
    try {
      const response = await api.post(`/mux/create-episode-upload/${episode.id}`);
      setUploadUrl(response.data.upload_url);
      setStatus('ready');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create upload URL');
      setStatus('error');
    }
  };

  const startPolling = () => {
    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/mux/episode-status/${episode.id}`);
        if (response.data.status === 'ready') {
          clearInterval(interval);
          setStatus('complete');
          setTimeout(() => {
            onUploadComplete();
            onClose();
          }, 1500);
        } else if (response.data.status === 'failed') {
          clearInterval(interval);
          setStatus('error');
          setError('Video processing failed');
        } else if (response.data.status === 'processing') {
          setStatus('processing');
        }
      } catch (err) {
        console.error('Error polling status:', err);
      }
    }, 3000);
    setPollInterval(interval);
  };

  const handleUploadStarted = () => {
    setStatus('uploading');
    setUploadProgress(0);
    
    // Start simulated progress as a fallback (will be overridden by real progress)
    let simProgress = 0;
    const simInterval = setInterval(() => {
      simProgress += Math.random() * 3;
      if (simProgress > 98) {
        simProgress = 98; // Cap at 98% until complete
        // If we've been at 98% for a while, start polling to check if actually complete
        startPollingEarly();
      }
      setUploadProgress(prev => {
        // Only update if simulated is higher (real progress will override)
        return prev < simProgress ? Math.round(simProgress) : prev;
      });
    }, 800);
    setSimulatedProgress(simInterval);
  };

  // Start polling early as a fallback when progress reaches near 100%
  const startPollingEarly = () => {
    if (pollInterval) return; // Already polling
    
    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/mux/episode-status/${episode.id}`);
        if (response.data.status === 'ready') {
          clearInterval(interval);
          if (simulatedProgress) {
            clearInterval(simulatedProgress);
            setSimulatedProgress(null);
          }
          setUploadProgress(100);
          setStatus('complete');
          setTimeout(() => {
            onUploadComplete();
            onClose();
          }, 1500);
        } else if (response.data.status === 'failed') {
          clearInterval(interval);
          if (simulatedProgress) {
            clearInterval(simulatedProgress);
            setSimulatedProgress(null);
          }
          setStatus('error');
          setError('Video processing failed');
        } else if (response.data.status === 'processing' || response.data.status === 'uploading') {
          // Upload complete, now processing
          if (simulatedProgress) {
            clearInterval(simulatedProgress);
            setSimulatedProgress(null);
          }
          setUploadProgress(100);
          setStatus('processing');
        }
      } catch (err) {
        console.error('Error polling status:', err);
      }
    }, 2000);
    setPollInterval(interval);
  };

  const handleUploadComplete = () => {
    if (simulatedProgress) {
      clearInterval(simulatedProgress);
      setSimulatedProgress(null);
    }
    setUploadProgress(100);
    setStatus('processing');
    startPolling();
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-background border border-white/10 rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h3 className="text-xl font-bold text-foreground">Upload Episode Video</h3>
            <p className="text-sm text-muted-foreground">S{episode.season} E{episode.episode_number}: {episode.title}</p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-white/10 rounded-lg"
            disabled={status === 'uploading' || status === 'processing'}
          >
            <X className="h-5 w-5 text-foreground/60" />
          </button>
        </div>

        <div className="p-6">
          {status === 'getting-url' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-electric animate-spin mb-3" />
              <p className="text-muted-foreground">Preparing upload...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-12 w-12 text-red-400 mb-3" />
              <p className="text-red-400 font-medium mb-2">Upload Error</p>
              <p className="text-muted-foreground text-sm text-center">{error}</p>
              <button
                onClick={getUploadUrl}
                className="mt-4 px-4 py-2 bg-white/10 text-foreground rounded-lg hover:bg-white/20"
              >
                Try Again
              </button>
            </div>
          )}

          {status === 'ready' && uploadUrl && (
            <div className="space-y-4">
              <div className="bg-white/5 border border-white/20 rounded-xl p-6">
                <p className="text-sm text-muted-foreground mb-4">
                  Select a video file to upload. Supported formats: MP4, MOV, MKV, WebM
                </p>
                <iframe 
                  src={`data:text/html,<html><body style="margin:0;display:flex;justify-content:center;align-items:center;height:100vh;background:%23111;font-family:system-ui;">
                    <script type="module" src="https://cdn.jsdelivr.net/npm/@mux/mux-uploader@latest"></script>
                    <mux-uploader 
                      endpoint="${uploadUrl}" 
                      style="--button-background-color:%2300F5D4;--progress-bar-fill-color:%2300F5D4;--button-hover-background-color:%2300D4B8;">
                    </mux-uploader>
                    <script>
                      const PARENT_ORIGIN = '${window.location.origin}';
                      const uploader = document.querySelector('mux-uploader');
                      let lastProgress = 0;
                      
                      uploader.addEventListener('uploadstart', () => {
                        window.parent.postMessage({type: 'upload-started'}, PARENT_ORIGIN);
                      });
                      
                      // Listen to multiple progress-related events
                      uploader.addEventListener('progress', (e) => {
                        let progress = 0;
                        if (typeof e.detail === 'number') {
                          progress = Math.round(e.detail * 100);
                        } else if (e.detail && typeof e.detail.percent === 'number') {
                          progress = Math.round(e.detail.percent);
                        } else if (e.detail && typeof e.detail.progress === 'number') {
                          progress = Math.round(e.detail.progress * 100);
                        }
                        if (progress !== lastProgress) {
                          lastProgress = progress;
                          window.parent.postMessage({type: 'upload-progress', progress: progress}, PARENT_ORIGIN);
                        }
                      });
                      
                      // Also monitor chunk progress for large files
                      uploader.addEventListener('chunkprogress', (e) => {
                        if (e.detail && typeof e.detail === 'number') {
                          const progress = Math.round(e.detail * 100);
                          window.parent.postMessage({type: 'upload-progress', progress: progress}, PARENT_ORIGIN);
                        }
                      });
                      
                      // Poll the uploader's internal progress as backup
                      const progressInterval = setInterval(() => {
                        const progressEl = uploader.shadowRoot?.querySelector('[role=progressbar]');
                        if (progressEl) {
                          const value = progressEl.getAttribute('aria-valuenow');
                          if (value) {
                            const progress = parseInt(value, 10);
                            if (progress > 0 && progress !== lastProgress) {
                              lastProgress = progress;
                              window.parent.postMessage({type: 'upload-progress', progress: progress}, PARENT_ORIGIN);
                            }
                          }
                        }
                      }, 500);
                      
                      uploader.addEventListener('success', () => {
                        clearInterval(progressInterval);
                        window.parent.postMessage({type: 'upload-progress', progress: 100}, PARENT_ORIGIN);
                        window.parent.postMessage({type: 'upload-complete'}, PARENT_ORIGIN);
                      });
                      
                      uploader.addEventListener('error', (e) => {
                        clearInterval(progressInterval);
                        window.parent.postMessage({type: 'upload-error', error: e.detail || 'Upload failed'}, PARENT_ORIGIN);
                      });
                    </script>
                  </body></html>`}
                  className="w-full h-40 rounded-lg border-0"
                  title="Episode Video Uploader"
                />
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Videos are processed by Mux for adaptive streaming
              </p>
            </div>
          )}

          {status === 'uploading' && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-full max-w-xs mb-6">
                {/* Progress Percentage */}
                <div className="text-center mb-4">
                  <span className="text-5xl font-bold text-electric">{uploadProgress}</span>
                  <span className="text-2xl font-bold text-electric">%</span>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-electric to-cyan-400 h-full rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                
                {/* Progress Steps */}
                <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
              
              <p className="text-foreground font-medium">Uploading video...</p>
              <p className="text-muted-foreground text-sm mt-1">Please don&apos;t close this window</p>
            </div>
          )}

          {status === 'processing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative">
                <Loader2 className="h-12 w-12 text-electric animate-spin" />
                <Video className="h-5 w-5 text-electric absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <p className="text-foreground font-medium mt-4">Processing video...</p>
              <p className="text-muted-foreground text-sm">This may take a few minutes</p>
            </div>
          )}

          {status === 'complete' && (
            <div className="flex flex-col items-center justify-center py-12">
              <CheckCircle className="h-12 w-12 text-green-400 mb-3" />
              <p className="text-green-400 font-medium">Upload Complete!</p>
              <p className="text-muted-foreground text-sm">Episode video is ready</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end p-6 border-t border-white/10">
          <button
            onClick={onClose}
            disabled={status === 'uploading' || status === 'processing'}
            className="px-6 py-2 bg-white/10 text-foreground rounded-lg hover:bg-white/20 disabled:opacity-50"
          >
            {status === 'complete' ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

const EpisodeModal = ({ episode, onClose, onSave, isNew = false, seasonNumber = 1 }) => {
  const [formData, setFormData] = useState(episode || {
    season: seasonNumber,
    episode_number: 1,
    title: '',
    description: '',
    video_url: '',
    thumbnail: '',
    duration: '',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [resolvingYoutube, setResolvingYoutube] = useState(false);
  const [youtubeError, setYoutubeError] = useState('');
  const [youtubeWarning, setYoutubeWarning] = useState('');
  const [youtubeInfo, setYoutubeInfo] = useState(null);
  const thumbnailInputRef = React.useRef(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleYoutubeFetch = async () => {
    const url = (formData.video_url || '').trim();
    if (!url) { setYoutubeError('Paste a YouTube URL first.'); return; }
    setYoutubeError('');
    setYoutubeWarning('');
    setYoutubeInfo(null);
    setResolvingYoutube(true);
    try {
      const res = await api.post('/admin/youtube-metadata', { url });
      handleChange('thumbnail', res.data.thumbnail_url);
      handleChange('video_url', res.data.watch_url);
      // Authoritative client-side embed probe
      const probe = await checkYouTubeEmbeddable(res.data.video_id);
      if (!probe.embeddable) {
        setYoutubeWarning(probe.reason || "This video isn't embeddable. Users won't be able to watch it. Pick a different upload.");
      } else if (res.data.yt_title) {
        setYoutubeInfo(`${res.data.yt_title}${res.data.yt_author ? ' — ' + res.data.yt_author : ''}`);
      } else {
        setYoutubeInfo('Ready to embed');
      }
    } catch (err) {
      setYoutubeError(err?.response?.data?.detail || 'Could not resolve YouTube URL.');
    } finally {
      setResolvingYoutube(false);
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload a JPEG, PNG, WebP, or GIF image.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File too large. Maximum size is 5MB.');
      return;
    }

    setUploadingThumbnail(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post('/admin/upload-thumbnail', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Get the base URL and construct full URL
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const thumbnailUrl = baseUrl + response.data.url;
      
      handleChange('thumbnail', thumbnailUrl);
    } catch (error) {
      alert('Failed to upload thumbnail: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      alert('Error saving episode: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-white/10 rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h3 className="text-xl font-bold text-foreground">
            {isNew ? 'Add Episode' : 'Edit Episode'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="h-5 w-5 text-foreground/60" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Season & Episode Number */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">Season</label>
              <input
                type="number"
                min="1"
                value={formData.season}
                onChange={(e) => handleChange('season', parseInt(e.target.value))}
                className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
                data-testid="episode-season-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">Episode #</label>
              <input
                type="number"
                min="1"
                value={formData.episode_number}
                onChange={(e) => handleChange('episode_number', parseInt(e.target.value))}
                className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
                data-testid="episode-number-input"
              />
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              required
              placeholder="Episode title"
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
              data-testid="episode-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">Description</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              placeholder="Episode description"
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground resize-none"
              data-testid="episode-description-input"
            />
          </div>

          {/* Thumbnail Upload - 16:9 */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">
              Episode Thumbnail (16:9 recommended)
            </label>
            <div className="space-y-3">
              {/* Thumbnail Preview */}
              <div 
                className="relative w-full aspect-video bg-white/5 border border-white/20 rounded-lg overflow-hidden cursor-pointer hover:border-electric/50 transition-colors"
                onClick={() => thumbnailInputRef.current?.click()}
              >
                {formData.thumbnail ? (
                  <img 
                    src={formData.thumbnail} 
                    alt="Thumbnail preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`absolute inset-0 flex flex-col items-center justify-center ${formData.thumbnail ? 'hidden' : 'flex'}`}
                  style={{ display: formData.thumbnail ? 'none' : 'flex' }}
                >
                  {uploadingThumbnail ? (
                    <Loader2 className="h-8 w-8 text-electric animate-spin" />
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground">Click to upload thumbnail</span>
                      <span className="text-xs text-muted-foreground/60 mt-1">16:9 aspect ratio recommended (e.g., 1920x1080)</span>
                    </>
                  )}
                </div>
                {formData.thumbnail && !uploadingThumbnail && (
                  <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white text-sm">Click to change thumbnail</span>
                  </div>
                )}
              </div>
              
              {/* Hidden file input */}
              <input
                ref={thumbnailInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleThumbnailUpload}
                className="hidden"
                data-testid="episode-thumbnail-file-input"
              />

              {/* URL input as fallback */}
              <div className="flex items-center space-x-2">
                <span className="text-xs text-muted-foreground">Or enter URL:</span>
                <input
                  type="url"
                  value={formData.thumbnail || ''}
                  onChange={(e) => handleChange('thumbnail', e.target.value)}
                  placeholder="https://example.com/thumbnail.jpg"
                  className="flex-1 px-3 py-1.5 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground text-sm"
                  data-testid="episode-thumbnail-input"
                />
              </div>
            </div>
          </div>

          {/* Video source: YouTube URL or Mux Upload */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground/80">Video URL (YouTube supported)</label>
            <div className="flex items-center space-x-2">
              <input
                type="url"
                value={formData.video_url || ''}
                onChange={(e) => handleChange('video_url', e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="flex-1 px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
                data-testid="episode-video-url-input"
              />
              <button
                type="button"
                onClick={handleYoutubeFetch}
                disabled={resolvingYoutube || !formData.video_url}
                className="px-4 py-2 bg-electric text-background rounded-lg font-medium hover:bg-electric/90 disabled:opacity-50 whitespace-nowrap flex items-center space-x-2"
                data-testid="episode-youtube-fetch-btn"
              >
                {resolvingYoutube ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />}
                <span>Use YouTube</span>
              </button>
            </div>
            {youtubeError && (
              <p className="text-xs text-red-400" data-testid="episode-youtube-error">{youtubeError}</p>
            )}
            {youtubeWarning && (
              <div className="flex items-start space-x-2 p-2 rounded-lg bg-orange-500/10 border border-orange-500/30" data-testid="episode-youtube-warning">
                <AlertCircle className="h-4 w-4 text-orange-400 shrink-0 mt-0.5" />
                <p className="text-xs text-orange-300">{youtubeWarning}</p>
              </div>
            )}
            {youtubeInfo && !youtubeWarning && (
              <p className="text-xs text-electric" data-testid="episode-youtube-info">
                ✓ Embed check passed · {youtubeInfo}
              </p>
            )}
            {formData.youtube_id && (
              <p className="text-xs text-green-400 flex items-center space-x-1">
                <CheckCircle className="h-3 w-3" />
                <span>YouTube video linked (ID: {formData.youtube_id}). Thumbnail auto-fetched.</span>
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Paste a YouTube link and click <span className="text-electric">Use YouTube</span> to pull the thumbnail automatically. Or leave this empty and use Mux upload (<Upload className="h-3 w-3 inline text-electric" /> button on the episode card).
            </p>
          </div>

          {/* Mux Status Info */}
          {formData.mux_playback_id && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-400" />
                <span className="text-green-400 text-sm font-medium">Video uploaded via Mux</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Playback ID: {formData.mux_playback_id}
              </p>
            </div>
          )}

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">Duration</label>
            <input
              type="text"
              value={formData.duration || ''}
              onChange={(e) => handleChange('duration', e.target.value)}
              placeholder="e.g., 45m or 1h 15m (auto-detected from upload)"
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
              data-testid="episode-duration-input"
            />
          </div>

          {/* Release Date */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">Release Date (PST)</label>
            <p className="text-xs text-muted-foreground mb-2">Episode will be locked until this date. Leave empty for immediate availability.</p>
            <input
              type="datetime-local"
              value={formData.release_date ? new Date(formData.release_date).toISOString().slice(0, 16) : ''}
              onChange={(e) => {
                if (e.target.value) {
                  const pstDate = new Date(e.target.value + ':00-07:00');
                  handleChange('release_date', pstDate.toISOString());
                } else {
                  handleChange('release_date', null);
                }
              }}
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground"
              data-testid="episode-release-date-input"
            />
            {formData.release_date && (
              <button
                type="button"
                onClick={() => handleChange('release_date', null)}
                className="mt-1 text-xs text-red-400 hover:text-red-300"
              >
                Clear release date
              </button>
            )}
          </div>
        </form>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 bg-white/10 text-foreground rounded-lg hover:bg-white/20"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || uploadingThumbnail}
            className="flex items-center space-x-2 px-6 py-2 bg-electric text-background rounded-lg font-medium hover:bg-electric/90 disabled:opacity-50"
            data-testid="save-episode-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            <span>{isNew ? 'Add Episode' : 'Save'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const EpisodeManager = ({ contentId, contentTitle, onClose }) => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [uploadingEpisode, setUploadingEpisode] = useState(null);
  const [expandedSeasons, setExpandedSeasons] = useState(new Set([1]));
  const [draggedEpisode, setDraggedEpisode] = useState(null);

  useEffect(() => {
    loadEpisodes();
  }, [contentId]);

  const loadEpisodes = async () => {
    try {
      const response = await api.get(`/admin/content/${contentId}/episodes`);
      setEpisodes(response.data.episodes || []);
    } catch (error) {
      console.error('Error loading episodes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEpisode = async (episodeData) => {
    await api.post(`/admin/content/${contentId}/episodes`, episodeData);
    loadEpisodes();
  };

  const handleUpdateEpisode = async (episodeData) => {
    const { id, content_id, created_at, ...updateData } = episodeData;
    await api.put(`/admin/content/${contentId}/episodes/${id}`, updateData);
    loadEpisodes();
  };

  const handleDeleteEpisode = async (episodeId) => {
    if (!window.confirm('Are you sure you want to delete this episode?')) return;
    try {
      await api.delete(`/admin/content/${contentId}/episodes/${episodeId}`);
      loadEpisodes();
    } catch (error) {
      alert('Error deleting episode');
    }
  };

  const handleDragStart = (e, episode) => {
    setDraggedEpisode(episode);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetEpisode) => {
    e.preventDefault();
    if (!draggedEpisode || draggedEpisode.id === targetEpisode.id) return;
    if (draggedEpisode.season !== targetEpisode.season) return;

    const seasonEpisodes = episodes.filter(ep => ep.season === targetEpisode.season);
    const draggedIndex = seasonEpisodes.findIndex(ep => ep.id === draggedEpisode.id);
    const targetIndex = seasonEpisodes.findIndex(ep => ep.id === targetEpisode.id);

    const newOrder = [...seasonEpisodes];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedEpisode);

    const episodeIds = newOrder.map(ep => ep.id);

    try {
      await api.post(`/admin/content/${contentId}/episodes/reorder`, { episode_ids: episodeIds });
      loadEpisodes();
    } catch (error) {
      console.error('Error reordering episodes:', error);
    }

    setDraggedEpisode(null);
  };

  const toggleSeason = (season) => {
    setExpandedSeasons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(season)) {
        newSet.delete(season);
      } else {
        newSet.add(season);
      }
      return newSet;
    });
  };

  // Group episodes by season
  const episodesBySeason = episodes.reduce((acc, episode) => {
    const season = episode.season || 1;
    if (!acc[season]) acc[season] = [];
    acc[season].push(episode);
    return acc;
  }, {});

  const seasons = Object.keys(episodesBySeason).map(Number).sort((a, b) => a - b);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <Loader2 className="h-8 w-8 text-electric animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-background border border-white/10 rounded-2xl w-full max-w-4xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h3 className="text-xl font-bold text-foreground">Manage Episodes</h3>
            <p className="text-sm text-muted-foreground">{contentTitle}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg" data-testid="close-episodes-modal">
            <X className="h-5 w-5 text-foreground/60" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* Add Episode Button */}
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setIsAddingNew(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-electric text-background rounded-lg font-medium hover:bg-electric/90"
              data-testid="add-episode-btn"
            >
              <Plus className="h-4 w-4" />
              <span>Add Episode</span>
            </button>
          </div>

          {/* Episodes List */}
          {episodes.length === 0 ? (
            <div className="text-center py-12 bg-white/5 rounded-xl">
              <Film className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No episodes yet</p>
              <p className="text-sm text-muted-foreground/60 mt-1">Click &quot;Add Episode&quot; to add your first episode</p>
            </div>
          ) : (
            <div className="space-y-4">
              {seasons.map(season => (
                <div key={season} className="bg-white/5 rounded-xl overflow-hidden">
                  {/* Season Header */}
                  <button
                    onClick={() => toggleSeason(season)}
                    className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                    data-testid={`season-${season}-header`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-lg font-semibold text-foreground">Season {season}</span>
                      <span className="px-2 py-0.5 bg-white/10 rounded-full text-xs text-muted-foreground">
                        {episodesBySeason[season].length} episodes
                      </span>
                    </div>
                    {expandedSeasons.has(season) ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    )}
                  </button>

                  {/* Episodes */}
                  {expandedSeasons.has(season) && (
                    <div className="border-t border-white/10">
                      {episodesBySeason[season]
                        .sort((a, b) => a.episode_number - b.episode_number)
                        .map((episode) => (
                          <div
                            key={episode.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, episode)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, episode)}
                            className={`flex items-center space-x-4 p-4 border-b border-white/5 last:border-b-0 hover:bg-white/5 transition-colors ${
                              draggedEpisode?.id === episode.id ? 'opacity-50' : ''
                            }`}
                            data-testid={`episode-${episode.id}`}
                          >
                            {/* Drag Handle */}
                            <div className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
                              <GripVertical className="h-5 w-5" />
                            </div>

                            {/* Episode Number */}
                            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-foreground font-bold">
                              {episode.episode_number}
                            </div>

                            {/* Thumbnail */}
                            <div className="w-24 h-14 rounded-lg bg-white/10 overflow-hidden flex-shrink-0">
                              {episode.thumbnail ? (
                                <img
                                  src={episode.thumbnail}
                                  alt={episode.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Play className="h-5 w-5 text-muted-foreground" />
                                </div>
                              )}
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <h4 className="text-foreground font-medium truncate">{episode.title}</h4>
                              <p className="text-sm text-muted-foreground truncate">
                                {episode.description || 'No description'}
                              </p>
                              {episode.duration && (
                                <span className="text-xs text-muted-foreground/60">{episode.duration}</span>
                              )}
                            </div>

                            {/* Status */}
                            <div className="flex items-center space-x-2">
                              {episode.mux_status === 'ready' && (
                                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center space-x-1">
                                  <CheckCircle className="h-3 w-3" />
                                  <span>Ready</span>
                                </span>
                              )}
                              {episode.mux_status === 'processing' && (
                                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full flex items-center space-x-1">
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  <span>Processing</span>
                                </span>
                              )}
                              {episode.mux_status === 'uploading' && (
                                <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full flex items-center space-x-1">
                                  <Upload className="h-3 w-3" />
                                  <span>Uploading</span>
                                </span>
                              )}
                              {episode.youtube_id && !episode.mux_status && (
                                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center space-x-1">
                                  <Video className="h-3 w-3" />
                                  <span>YouTube</span>
                                </span>
                              )}
                              {!episode.mux_status && !episode.youtube_id && episode.video_url && (
                                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                                  URL Set
                                </span>
                              )}
                              {!episode.mux_status && !episode.youtube_id && !episode.video_url && (
                                <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                                  No Video
                                </span>
                              )}
                            </div>

                            {/* Actions */}
                            <div className="flex space-x-2">
                              <button
                                onClick={() => setUploadingEpisode(episode)}
                                className="p-2 hover:bg-electric/20 rounded-lg transition-colors"
                                title="Upload Video"
                                data-testid={`upload-episode-${episode.id}`}
                              >
                                <Upload className="h-4 w-4 text-electric" />
                              </button>
                              <button
                                onClick={() => setEditingEpisode(episode)}
                                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                title="Edit"
                                data-testid={`edit-episode-${episode.id}`}
                              >
                                <Save className="h-4 w-4 text-muted-foreground hover:text-electric" />
                              </button>
                              <button
                                onClick={() => handleDeleteEpisode(episode.id)}
                                className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                                title="Delete"
                                data-testid={`delete-episode-${episode.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-red-400" />
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-white/10">
          <p className="text-sm text-muted-foreground">
            Drag episodes to reorder • Click <Upload className="h-4 w-4 inline text-electric" /> to upload video via Mux
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-electric text-background rounded-lg font-medium hover:bg-electric/90"
            data-testid="done-episodes-btn"
          >
            Done
          </button>
        </div>
      </div>

      {/* Add Episode Modal */}
      {isAddingNew && (
        <EpisodeModal
          isNew
          seasonNumber={seasons[seasons.length - 1] || 1}
          onClose={() => setIsAddingNew(false)}
          onSave={handleCreateEpisode}
        />
      )}

      {/* Edit Episode Modal */}
      {editingEpisode && (
        <EpisodeModal
          episode={editingEpisode}
          onClose={() => setEditingEpisode(null)}
          onSave={handleUpdateEpisode}
        />
      )}

      {/* Upload Episode Video Modal */}
      {uploadingEpisode && (
        <EpisodeUploadModal
          episode={uploadingEpisode}
          onClose={() => setUploadingEpisode(null)}
          onUploadComplete={loadEpisodes}
        />
      )}
    </div>
  );
};

export default EpisodeManager;
